import React, { useState, useEffect } from 'react'
import { Modal, Button, Select, Group, FileInput, Stack, Text, LoadingOverlay, Grid, Divider, Center, Paper, Loader, Box } from '@mantine/core'
import { IconUpload, IconFileSpreadsheet, IconArrowRight, IconPlaneDeparture, IconPlaneArrival } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { getAllProvinceList, getAllCityList } from '../../../../../services/location'
import { importLogisticRates } from '../../../../../services/global-settings'


const ModalImportRate = ({ opened, onClose, sourceCountry, destCountry, onSuccess }) => {
    const [loading, setLoading] = useState(false)
    const [file, setFile] = useState(null)

    const [originType, setOriginType] = useState('Country')
    const [destType, setDestType] = useState('Country')

    const [originId, setOriginId] = useState(null)
    const [destId, setDestId] = useState(null)

    const [originOptions, setOriginOptions] = useState([])
    const [destOptions, setDestOptions] = useState([])

    const [optionsLoading, setOptionsLoading] = useState({ origin: false, dest: false })

    useEffect(() => {
        if (opened) {
            setOriginType('Country')
            setDestType('Country')
            setFile(null)

            setOriginId(null)
            setDestId(null)
        }
    }, [opened])

    useEffect(() => {
        const fetchOptions = async (type, countryId, setOptions, key) => {
            if (type === 'Country' || !countryId) {
                setOptions([])
                return
            }

            setOptionsLoading(prev => ({ ...prev, [key]: true }))
            try {
                let response
                if (type === 'Province') {
                    response = await getAllProvinceList({
                        'filter[country_id][is]': countryId,
                        'page[limit]': 1000
                    })
                } else if (type === 'City') {
                    response = await getAllCityList({
                        'filter[country_id][is]': countryId,
                        'page[limit]': 1000
                    })
                }

                if (response?.success) {
                    setOptions(response.data.map(item => ({
                        value: String(item.id),
                        label: item.attributes.name
                    })))
                }
            } catch (error) {
                console.error(`Failed to fetch ${type}`, error)
            } finally {
                setOptionsLoading(prev => ({ ...prev, [key]: false }))
            }
        }

        fetchOptions(originType, sourceCountry?.id, setOriginOptions, 'origin')
    }, [originType, sourceCountry])

    useEffect(() => {
        const fetchOptions = async (type, countryId, setOptions, key) => {
            if (type === 'Country' || !countryId) {
                setOptions([])
                return
            }

            setOptionsLoading(prev => ({ ...prev, [key]: true }))
            try {
                let response
                if (type === 'Province') {
                    response = await getAllProvinceList({
                        'filter[country_id][is]': countryId,
                        'page[limit]': 1000
                    })
                } else if (type === 'City') {
                    response = await getAllCityList({
                        'filter[country_id][is]': countryId,
                        'page[limit]': 1000
                    })
                }

                if (response?.success) {
                    setOptions(response.data.map(item => ({
                        value: String(item.id),
                        label: item.attributes.name
                    })))
                }
            } catch (error) {
                console.error(`Failed to fetch ${type}`, error)
            } finally {
                setOptionsLoading(prev => ({ ...prev, [key]: false }))
            }
        }

        fetchOptions(destType, destCountry?.id, setDestOptions, 'dest')
    }, [destType, destCountry])

    const handleImport = async () => {
        if (!file) {
            notifications.show({ title: 'Error', message: 'Please select a file to upload', color: 'red' })
            return
        }

        if (originType !== 'Country' && !originId) {
            notifications.show({ title: 'Error', message: `Please select an origin ${originType.toLowerCase()}`, color: 'red' })
            return
        }

        if (destType !== 'Country' && !destId) {
            notifications.show({ title: 'Error', message: `Please select a destination ${destType.toLowerCase()}`, color: 'red' })
            return
        }

        setLoading(true)
        try {
            const formData = new FormData()
            formData.append('file_to_import', file)
            formData.append('shipping_type', 'international')

            formData.append('origin_type', originType.toLowerCase())
            formData.append('destination_type', destType.toLowerCase())

            // sourceId and destId are the base country IDs
            formData.append('origin_id', String(sourceCountry.id))
            formData.append('destination_id', String(destCountry.id))

            // Specific IDs based on type
            formData.append('origin_country_id', String(sourceCountry.id))
            if (originType === 'Province') {
                formData.append('origin_province_id', originId)
            } else if (originType === 'City') {
                formData.append('origin_city_id', originId)
            }

            formData.append('destination_country_id', String(destCountry.id))
            if (destType === 'Province') {
                formData.append('destination_province_id', destId)
            } else if (destType === 'City') {
                formData.append('destination_city_id', destId)
            }

            const response = await importLogisticRates(formData)
            if (response.success || response.status === 200) {
                notifications.show({ title: 'Success', message: 'Rates imported successfully', color: 'green' })
                onSuccess()
                onClose()
            } else {
                notifications.show({ title: 'Error', message: response.message || 'Failed to import rates', color: 'red' })
            }
        } catch (error) {
            console.error('Import failed', error)
            notifications.show({ title: 'Error', message: 'An error occurred during import', color: 'red' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={700} size="lg">Bulk Import Logistic Rates</Text>}
            size="lg"
            centered
            radius="md"
        >
            <Stack pos="relative">
                <LoadingOverlay visible={loading} />

                <Box>
                    <Grid align="stretch" gutter="md">
                        {/* Origin Section */}
                        <Grid.Col span={{ base: 12, sm: 5.5 }}>
                            <Paper withBorder p="md" radius="md" h="100%" bg="gray.0">
                                <Stack gap="md">
                                    <Group gap="xs">
                                        <IconPlaneDeparture size={20} color="var(--mantine-color-blue-6)" />
                                        <Text fw={700} c="blue">ORIGIN</Text>
                                    </Group>

                                    <Divider />

                                    <Stack gap={4}>
                                        <Text size="xs" c="dimmed" fw={500}>Country</Text>
                                        <Text fw={600}>{sourceCountry?.attributes?.name || '-'}</Text>
                                    </Stack>

                                    <Select
                                        label="Method"
                                        description="Select geographic level"
                                        value={originType}
                                        onChange={(val) => {
                                            setOriginType(val)
                                            setOriginId(null)
                                        }}
                                        data={[
                                            { value: 'Country', label: 'Country Only' },
                                            { value: 'Province', label: 'Province' },
                                            { value: 'City', label: 'City' }
                                        ]}
                                    />

                                    {originType !== 'Country' && (
                                        <Select
                                            label={`Select ${originType}`}
                                            placeholder={`Choose ${originType.toLowerCase()}`}
                                            value={originId}
                                            onChange={setOriginId}
                                            data={originOptions}
                                            searchable
                                            clearable
                                            disabled={optionsLoading.origin}
                                            rightSection={optionsLoading.origin ? <Loader size="xs" /> : null}
                                        />
                                    )}
                                </Stack>
                            </Paper>
                        </Grid.Col>

                        {/* Divider */}
                        <Grid.Col span={{ base: 12, sm: 1 }} visibleFrom="sm">
                            <Center h="100%">
                                <IconArrowRight size={32} color="var(--mantine-color-gray-4)" />
                            </Center>
                        </Grid.Col>

                        {/* Destination Section */}
                        <Grid.Col span={{ base: 12, sm: 5.5 }}>
                            <Paper withBorder p="md" radius="md" h="100%" bg="gray.0">
                                <Stack gap="md">
                                    <Group gap="xs">
                                        <IconPlaneArrival size={20} color="var(--mantine-color-green-6)" />
                                        <Text fw={700} c="green">DESTINATION</Text>
                                    </Group>

                                    <Divider />

                                    <Stack gap={4}>
                                        <Text size="xs" c="dimmed" fw={500}>Country</Text>
                                        <Text fw={600}>{destCountry?.attributes?.name || '-'}</Text>
                                    </Stack>

                                    <Select
                                        label="Method"
                                        description="Select geographic level"
                                        value={destType}
                                        onChange={(val) => {
                                            setDestType(val)
                                            setDestId(null)
                                        }}
                                        data={[
                                            { value: 'Country', label: 'Country Only' },
                                            { value: 'Province', label: 'Province' },
                                            { value: 'City', label: 'City' }
                                        ]}
                                    />

                                    {destType !== 'Country' && (
                                        <Select
                                            label={`Select ${destType}`}
                                            placeholder={`Choose ${destType.toLowerCase()}`}
                                            value={destId}
                                            onChange={setDestId}
                                            data={destOptions}
                                            searchable
                                            clearable
                                            disabled={optionsLoading.dest}
                                            rightSection={optionsLoading.dest ? <Loader size="xs" /> : null}
                                        />
                                    )}
                                </Stack>
                            </Paper>
                        </Grid.Col>
                    </Grid>
                </Box>

                <Divider my="sm" />

                <Paper withBorder p="md" radius="md" bg="blue.0" style={{ borderStyle: 'dashed', borderWidth: 2 }}>
                    <FileInput
                        label="Excel File"
                        description="Only support .xlsx and .xls format"
                        placeholder="Click to upload or drag file here"
                        accept=".xlsx, .xls"
                        leftSection={<IconFileSpreadsheet size={20} color="var(--mantine-color-blue-6)" />}
                        value={file}
                        onChange={setFile}
                        required
                        variant="unstyled"
                        styles={{
                            input: { cursor: 'pointer', textAlign: 'center', fontWeight: 600 }
                        }}
                    />
                </Paper>

                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button
                        leftSection={<IconUpload size={16} />}
                        onClick={handleImport}
                        loading={loading}
                        color="red"
                    >
                        Import Rates
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

export default ModalImportRate
